import { Box, CardActions, CardContent, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { ActionTriggers } from './ActionTriggers'
import { SquareIconButton } from '@/components/SquareIconButton/SquareIconButton.tsx'
import { useEffect, useMemo, useRef } from 'react'
import { blackVariants } from '@/themes/colors'
import type { ActionProps } from './SnackbarNotification'
import { styled } from '@mui/material/styles'
import { getColorFromVariant } from './variantUtils'
import { getAlertIconMarkupFormat } from '../Icons/AlertIcon'
import type { BaseStatusVariant } from '@/types/status.types'

const FeedbackMessageBody = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 300,
  color: theme.palette.grey[700],
  lineHeight: 1.15
}))

const GradientStack = styled(Stack)<{ gradientColor: string }>(({
  gradientColor,
  theme
}) => {
  return {
    width: '100%',
    background: `linear-gradient(90deg, ${gradientColor} 0%, #FFFFFF 33%)`,
    backdropFilter: 'blur(20.5px)',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`
  }
})

interface FeedbackMessageProps {
  variant: BaseStatusVariant
  title?: string
  message: string
  mainActionButton?: ActionProps
  secondaryActionButton?: ActionProps
  actionLink?: ActionProps
  onClose?: () => void
  scrollIntoViewOnShow?: boolean
}

export const FeedbackMessageContent = ({
  variant,
  title,
  message,
  mainActionButton,
  secondaryActionButton,
  actionLink,
  onClose,
  scrollIntoViewOnShow = false
}: FeedbackMessageProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const { mainColor, gradientColor } = useMemo(
    () => getColorFromVariant(variant),
    [variant]
  )

  useEffect(() => {
    if (scrollIntoViewOnShow) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [scrollIntoViewOnShow])

  const Icon = getAlertIconMarkupFormat(variant, mainColor)

  return (
    <GradientStack
      ref={ref}
      justifyContent="flex-start"
      gradientColor={gradientColor}
    >
      <CardContent sx={{ width: '100%', '&:last-child': { pb: 2 } }}>
        <Stack direction="row" gap={2} alignItems="flex-start" alignSelf="stretch">
          <Stack direction="column" gap={1}>
            <Stack direction="row" gap={1} flexGrow={1} alignItems="flex-start">
              {Icon}
              {title ? (
                <Typography
                  color={mainColor}
                  sx={{ fontWeight: 600 }}
                  variant="subtitle3"
                >
                  {title}
                </Typography>
              ) : (
                <FeedbackMessageBody>{message}</FeedbackMessageBody>
              )}
            </Stack>
            {title && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                <FeedbackMessageBody>{message}</FeedbackMessageBody>
              </Box>
            )}
          </Stack>
          {onClose && (
            <SquareIconButton size="small" onClick={onClose} sx={{ padding: 0 }}>
              <CloseIcon fontSize="small" sx={{ color: blackVariants[500] }} />
            </SquareIconButton>
          )}
        </Stack>
      </CardContent>
      {(mainActionButton || secondaryActionButton || actionLink) && (
        <CardActions sx={{ p: 2, pt: 0 }}>
          <ActionTriggers
            color={variant === 'default' ? 'primary' : variant}
            mainActionButton={mainActionButton}
            secondaryActionButton={secondaryActionButton}
            actionLink={actionLink}
          />
        </CardActions>
      )}
    </GradientStack>
  )
}
